// Returns [{ entity, id, reasons }] for entities that have duplicate values
// for at least one of the given keys (same key AND same value)
export function getEntitiesWithDuplicateValuesForKeys(dataSource, keyNames = []) {
  if (!dataSource || !dataSource.entities || !Array.isArray(keyNames) || keyNames.length === 0) {
    return [];
  }

  const requiredKeys = keyNames.map((k) => String(k).trim()).filter(Boolean);
  if (requiredKeys.length === 0) return [];

  const entities = dataSource.entities.values || dataSource.entities;
  if (!Array.isArray(entities) || entities.length === 0) return [];

  function getPropertyValueFromProps(props, key) {
    if (!props) return undefined;

    if (typeof props.get === "function") {
      const property = props.get(key);
      if (!property) return undefined;
      if (typeof property.getValue === "function") return property.getValue();
      return property;
    }

    if (props._properties && typeof props._properties === "object") {
      const property = props._properties[key];
      if (!property) return undefined;
      if (typeof property.getValue === "function") return property.getValue();
      return property;
    }

    if (typeof props.getValue === "function") {
      const obj = props.getValue();
      if (obj && typeof obj === "object") return obj[key];
    }

    if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];

    return undefined;
  }

  function getEntityId(entity) {
    if (!entity) return undefined;
    const props = entity.properties;
    let idFromProps;

    if (props) {
      if (typeof props.get === "function") {
        const property = props.get("id");
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
        const property = props._properties.id;
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && typeof props.getValue === "function") {
        const obj = props.getValue();
        if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "id")) {
          idFromProps = obj.id;
        }
      }

      if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
        idFromProps = props.id;
      }
    }

    if (idFromProps !== undefined && idFromProps !== null) return idFromProps;
    return entity.id;
  }

  function normalizeValue(v) {
    return String(v);
  }

  const result = [];
  const seenIds = new Set();

  // Use a map to accumulate reasons per entity (in case multiple keys duplicate)
  const reasonsByEntity = new Map(); // dedupeKey -> { entity, id, reasons: [] }

  for (const key of requiredKeys) {
    const valueMap = new Map(); // normValue -> [entities]

    // Group entities by this key's value
    for (const entity of entities) {
      if (!entity || !entity.properties) continue;

      let value = getPropertyValueFromProps(entity.properties, key);

      if (value === undefined && key === "id" && entity.id !== undefined) {
        value = entity.id;
      }

      if (value === null || value === undefined) continue;

      const norm = normalizeValue(value);

      let arr = valueMap.get(norm);
      if (!arr) {
        arr = [];
        valueMap.set(norm, arr);
      }
      arr.push(entity);
    }

    // Any value that appears at least twice => duplicates
    for (const [normValue, ents] of valueMap.entries()) {
      if (!ents || ents.length < 2) continue;

      for (const e of ents) {
        const id = getEntityId(e);
        const dedupeKey = id ?? e;

        let bucket = reasonsByEntity.get(dedupeKey);
        if (!bucket) {
          bucket = { entity: e, id, reasons: [] };
          reasonsByEntity.set(dedupeKey, bucket);
        }

        bucket.reasons.push(`Duplicate value for key: ${key} (value: ${normValue})`);
      }
    }
  }

  // Convert map to array and dedupe reasons
  for (const [dedupeKey, item] of reasonsByEntity.entries()) {
    if (!seenIds.has(dedupeKey)) {
      seenIds.add(dedupeKey);
      item.reasons = Array.from(new Set(item.reasons));
      result.push(item);
    }
  }

  return result;
}
